import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, SpinnerGap } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function PaymentResult() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isCancel = pathname.includes("cancel");
  const sessionId = new URLSearchParams(search).get("session_id");
  const [state, setState] = useState(isCancel ? "cancel" : "polling"); // polling|success|failed|cancel
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState("failed"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") { setState("success"); return; }
      if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      if (attempts >= 8) { setState("failed"); return; }
      setTimeout(() => setAttempts((a) => a + 1), 2000);
    } catch {
      if (attempts >= 8) setState("failed");
      else setTimeout(() => setAttempts((a) => a + 1), 2000);
    }
  }, [sessionId, attempts]);

  useEffect(() => { if (state === "polling") poll(); }, [state, attempts, poll]);

  const cfg = {
    polling: { icon: <SpinnerGap size={56} className="text-primary animate-spin" />, title: "Confirming payment…", sub: "Hang tight while we verify your transaction." },
    success: { icon: <CheckCircle size={56} weight="fill" className="text-primary" />, title: "You're in.", sub: "Payment confirmed — enjoy your fantasy." },
    failed: { icon: <XCircle size={56} weight="fill" className="text-destructive" />, title: "Payment issue", sub: "We couldn't confirm this payment. If you were charged, it will reflect shortly." },
    cancel: { icon: <XCircle size={56} weight="fill" className="text-muted-foreground" />, title: "Checkout cancelled", sub: "No worries — your card was not charged." },
  }[state];

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6" data-testid="payment-result">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="text-center max-w-md">
        <div className="flex justify-center mb-6">{cfg.icon}</div>
        <h1 data-testid="payment-title" className="font-display text-5xl mb-3">{cfg.title}</h1>
        <p className="text-muted-foreground mb-8">{cfg.sub}</p>
        <div className="flex gap-3 justify-center">
          <button data-testid="payment-feed" onClick={() => navigate("/feed")}
            className="px-8 py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95">Go to feed</button>
          <button data-testid="payment-explore" onClick={() => navigate("/explore")}
            className="px-8 py-4 border border-border font-accent font-bold uppercase tracking-widest text-sm hover:border-foreground transition-colors">Explore</button>
        </div>
      </motion.div>
    </div>
  );
}
